import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiShield, FiCheck, FiX as FiDecline } from 'react-icons/fi';

interface CookieConsentProps {
  onAccept?: () => void;
  onDecline?: () => void;
}

const CookieConsent: React.FC<CookieConsentProps> = ({ onAccept, onDecline }) => {
  const [showConsent, setShowConsent] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check of cookie consent al is gegeven
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowConsent(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowConsent(false);
    if (onAccept) onAccept();
  };

  const handleDecline = () => {
    localStorage.setItem('cookieConsent', 'declined');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowConsent(false);
    if (onDecline) onDecline();
  };

  const handleAcceptEssential = () => {
    localStorage.setItem('cookieConsent', 'essential');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowConsent(false);
    if (onAccept) onAccept();
  };

  if (!showConsent) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed bottom-0 left-0 right-0 z-50 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 shadow-lg"
      >
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            {/* Cookie Icon en Titel */}
            <div className="flex items-start space-x-3">
              <div className="bg-blue-100 dark:bg-blue-900 p-2 rounded-lg">
                <FiShield className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Cookie Instellingen
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                  Wij gebruiken cookies om uw ervaring op onze website te verbeteren. 
                  Sommige cookies zijn noodzakelijk voor de werking van de site, 
                  andere helpen ons om de site te verbeteren.
                </p>
                
                {/* Details Toggle */}
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline focus:outline-none"
                >
                  {showDetails ? 'Verberg details' : 'Bekijk details'}
                </button>

                {/* Cookie Details */}
                <AnimatePresence>
                  {showDetails && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="mt-3 space-y-2"
                    >
                      <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-1">
                          Noodzakelijke Cookies
                        </h4>
                        <p className="text-xs text-gray-600 dark:text-gray-300">
                          Deze cookies zijn essentieel voor de werking van de website en kunnen niet worden uitgeschakeld.
                        </p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-1">
                          Analytics Cookies
                        </h4>
                        <p className="text-xs text-gray-600 dark:text-gray-300">
                          Deze cookies helpen ons om te begrijpen hoe bezoekers de website gebruiken door informatie anoniem te verzamelen.
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Actie Knoppen */}
            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
              <button
                onClick={handleDecline}
                className="flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors duration-200"
              >
                <FiDecline className="mr-2 h-4 w-4" />
                Alleen Noodzakelijk
              </button>
              
              <button
                onClick={handleAcceptEssential}
                className="flex items-center justify-center px-4 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900 hover:bg-blue-200 dark:hover:bg-blue-800 rounded-lg transition-colors duration-200"
              >
                <FiShield className="mr-2 h-4 w-4" />
                Accepteer Essentieel
              </button>
              
              <button
                onClick={handleAccept}
                className="flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-200"
              >
                <FiCheck className="mr-2 h-4 w-4" />
                Accepteer Alles
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsent; 